import type { Configuration } from '../..';
import HttpMethod from '../../request/HttpMethod';
import Requester from '../../request/Requester';
import type { IRequest } from '../../request/Types';
import type { RiskRecognitionArchiveResult, RiskRecognitionHighlight, RiskRecognitionListParams, SilenceReason } from './Types';

export class RiskRecognitionsRequester {
  private requester: Requester;

  constructor(configuration: Configuration) {
    this.requester = new Requester(configuration);
  }

  /**
   * Lista as notificações de risk-recognition da frota (escopo via bearer/X-Fleet-Id do bouncer),
   * sempre mais-recente-primeiro. Array vazio = nada corresponde ao filtro / frota nunca disparou.
   * `apiHost`, sem prefixo `/herbie-1.1` — mesmo serviço (ai-ingest). Paginação por keyset (`since`/`before`),
   * nunca offset; sem filtro de janela/criticidade no servidor.
   */
  public listHighlights = (params: RiskRecognitionListParams = {}): Promise<RiskRecognitionHighlight[]> => {
    const requestData: IRequest = {
      endpoint: '/v1/risk-recognitions/highlights',
      method: HttpMethod.GET,
      params: {
        since: params.since,
        before: params.before,
        limit: params.limit,
        archived: params.archived === undefined ? undefined : String(params.archived),
        archived_by: params.archived_by,
      },
    };

    return this.requester.request(requestData).then((response) => response.data);
  };

  /** Arquiva (fleet-wide, permanente). Idempotente: re-arquivar mantém `archived_by`/`archived_at` originais. */
  public archiveHighlight = (detectionId: string): Promise<RiskRecognitionArchiveResult> => {
    const requestData: IRequest = {
      // Encode: `detectionId` é string pública; sem encode, um `/`/`?`/`#` montaria outra rota.
      endpoint: `/v1/risk-recognitions/highlights/${encodeURIComponent(detectionId)}/archive`,
      method: HttpMethod.POST,
    };

    return this.requester.request(requestData).then((response) => response.data);
  };

  /** Limpa o arquivamento. Idempotente (204). */
  public unarchiveHighlight = (detectionId: string): Promise<void> => {
    const requestData: IRequest = {
      endpoint: `/v1/risk-recognitions/highlights/${encodeURIComponent(detectionId)}/archive`,
      method: HttpMethod.DELETE,
    };

    return this.requester.request(requestData).then(() => undefined);
  };

  /**
   * Silence a detection's highlight. Distinto de "archive": só suprime o highlight (não muda o estado de
   * listagem). Fire-and-forget: o 201 body (criticality_ceiling, expires_at, …) é ignorado.
   */
  public createSilence = (detectionId: string, reason: SilenceReason): Promise<void> => {
    const requestData: IRequest = {
      endpoint: `/v1/risk-recognitions/detections/${detectionId}/silences`,
      method: HttpMethod.POST,
      body: JSON.stringify({ reason }),
    };

    return this.requester.request(requestData).then(() => undefined);
  };
}

export default RiskRecognitionsRequester;
